/**
 * 导购Agent - 知识接口（商品知识 / 店铺知识）
 *
 * 对齐后端《导购商品管理》文档接口 12~17。两类知识结构一致，差别只在归属维度：
 * - 商品知识挂在具体商品下（product_id），店铺知识是账号（门店）级通用问答
 * - 分页同商品接口，用 page / page_size，不是惯用的 per_page
 *
 * 知识主要由商品同步与算法侧产出，另提供人工补录入口（接口 18 / 19）：
 * 新增只回 { id }，拿不到向量状态等字段，且同问题重复提交是覆盖旧答案而非新增行，
 * 因此调用方新增成功后应重拉列表，不要本地拼记录插入。
 */
import { apiDelete, apiGet, apiPost, apiRequest } from '@/utils/request';
import { parsePaginatedResponse } from '@/utils/apiResponse';
import { GUIDE_KNOWLEDGE_PAGE_SIZE } from '@/constants/guideAgent';
import type {
  GuideKnowledgeCreateResult,
  GuideKnowledgeUpdatePayload,
  GuideProductKnowledge,
  GuideProductKnowledgeCreatePayload,
  GuideProductKnowledgeListParams,
  GuideStore,
  GuideStoreSyncStatus,
  GuideStoreSyncSubmitResult,
  GuideStoreKnowledge,
  GuideStoreKnowledgeCreatePayload,
  GuideStoreKnowledgeListParams,
  GuideStoreListParams,
} from '@/types/guideAgent';

/** 商品知识：GET/POST/PUT/DELETE /product-knowledge */
export const guideProductKnowledgeApi = {
  list: (params: GuideProductKnowledgeListParams = {}) =>
    apiGet('/product-knowledge', { page_size: GUIDE_KNOWLEDGE_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse<GuideProductKnowledge>(d),
    ),
  /**
   * 人工补录一条商品知识，恒落草稿版本（version_type=1）。
   * 归属（robot_id/account_id/source_product_id）由后端按 product_id 反查，前端只传商品主键。
   */
  create: (data: GuideProductKnowledgeCreatePayload) =>
    apiPost<GuideKnowledgeCreateResult>('/product-knowledge', data),
  /** 改问题/答案会重置向量状态，由后端异步重新向量化 */
  update: (id: number, data: GuideKnowledgeUpdatePayload) =>
    apiRequest<GuideProductKnowledge>(`/product-knowledge/${id}`, { method: 'PUT', data }),
  /** 软删除，同时同步算法侧 */
  remove: (id: number) => apiDelete(`/product-knowledge/${id}`),
};

/** 店铺知识：GET/POST/PUT/DELETE /store-knowledge */
export const guideStoreKnowledgeApi = {
  list: (params: GuideStoreKnowledgeListParams = {}) =>
    apiGet('/store-knowledge', { page_size: GUIDE_KNOWLEDGE_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse<GuideStoreKnowledge>(d),
    ),
  /**
   * 人工补录一条店铺知识，恒落草稿版本（version_type=1）。
   * 归属账号由 account_id 指定（须是该机器人绑定的抖音账号）；不传时后端按 robot_id
   * 取唯一绑定账号，绑定多个则报 422，错误文案由响应拦截器统一提示。
   */
  create: (data: GuideStoreKnowledgeCreatePayload) =>
    apiPost<GuideKnowledgeCreateResult>('/store-knowledge', data),
  update: (id: number, data: GuideKnowledgeUpdatePayload) =>
    apiRequest<GuideStoreKnowledge>(`/store-knowledge/${id}`, { method: 'PUT', data }),
  remove: (id: number) => apiDelete(`/store-knowledge/${id}`),
};

/** 门店库一次拉多少条，接口上限 100 */
const STORE_LIBRARY_PAGE_SIZE = 100;
/** 最多拉几页，2000 家封顶；再多就该用筛选缩范围，别把浏览器拖垮 */
const STORE_LIBRARY_MAX_PAGES = 20;

/** 门店库：GET /store-library（算法侧回写，前端只读；仅营业状态可人工批量修正） */
export const guideStoreLibraryApi = {
  /**
   * 提交手动同步任务：POST /store-library/sync。
   * 后端异步执行，同一机器人已有未完成任务时不新建、直接复用并回 already=true。
   */
  sync: (robotId: number) =>
    apiPost<GuideStoreSyncSubmitResult>('/store-library/sync', { robot_id: robotId }),

  /** 查该机器人最近一次门店同步任务状态，供轮询；只看 fetched，其余计数后端不维护 */
  syncStatus: (robotId: number) =>
    apiGet<GuideStoreSyncStatus>('/store-library/sync-status', { robot_id: robotId }),

  /**
   * 批量改营业状态：PUT /store-library/status。
   * poi_ids 为平台门店 ID 逗号分隔串，返回实际更新的行数（库里不存在的不计入）。
   */
  updateStatus: (poiIds: string, status: 0 | 1) =>
    apiRequest<{ updated: number }>('/store-library/status', {
      method: 'PUT',
      data: { poi_ids: poiIds, status },
    }),

  list: (params: GuideStoreListParams = {}) =>
    apiGet('/store-library', { page_size: STORE_LIBRARY_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse<GuideStore>(d),
    ),

  /**
   * 拉全量门店：先取第一页拿总数，剩下的页并发拉。
   *
   * 地图要看的是门店的完整分布，只给一页点没有意义，所以一次拉齐交给前端筛。
   * truncated 为 true 表示总数超过了封顶页数，界面上要提示用户用筛选缩小范围。
   */
  listAll: async (params: GuideStoreListParams = {}) => {
    const first = await guideStoreLibraryApi.list({ ...params, page: 1 });
    const totalPages = Math.ceil(first.total / STORE_LIBRARY_PAGE_SIZE);
    const pages = Math.min(totalPages, STORE_LIBRARY_MAX_PAGES);
    if (pages <= 1) {
      return {
        list: first.list,
        total: first.total,
        truncated: totalPages > STORE_LIBRARY_MAX_PAGES,
      };
    }
    const rest = await Promise.all(
      Array.from({ length: pages - 1 }, (_, i) =>
        guideStoreLibraryApi.list({ ...params, page: i + 2 }),
      ),
    );
    return {
      list: [...first.list, ...rest.flatMap((r) => r.list)],
      total: first.total,
      truncated: totalPages > STORE_LIBRARY_MAX_PAGES,
    };
  },
};
